import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { DeadlineNote, AutoApprovedNote } from '../components/ApprovalBits.jsx';
import { useAuth } from '../AuthContext.jsx';
import { clientPath, useFollowSwitcher } from '../lib/clientLink.js';
import PostReview from '../components/PostReview.jsx';
import { fmtDate } from '../components/FormBits.jsx';
import Working, { pollUntil } from '../components/Working.jsx';
import { ReplaceControl, ReplaceState, ReplaceReview, FailedReplace } from '../components/ReplaceBits.jsx';

// Session 5 Part D: agency review of a quarterly plan. Inline edits go
// through portal_edit_plan_item; approval through portal_plan_agency_approve.
// Session 20: per-post Approve / Replace (PostReview, the same screen the
// client uses), saved as you go; the bottom button sends only the replacements
// or approves. Posts the client already approved are locked. Inline title
// edit stays. A sent plan shows the client's decisions so far, read-only.

export default function AdminPlan() {
  const { jobId } = useParams();
  const { isAgency, setActiveId, activeId } = useAuth();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});      // inline title/keyword edits, {index: {field: value}}
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);
  const [editing, setEditing] = useState(null);  // index with the title editor open

  const load = useCallback(async () => {
    const { data, error: err } = await supabase.from('v_portal_plan_jobs').select('*').eq('job_id', jobId).maybeSingle();
    if (err) { setError(err.message); return; }
    setJob(data || false);
    setDrafts({}); setEditing(null);
  }, [jobId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (job && job.client_id !== activeId) setActiveId(job.client_id); }, [job]); // eslint-disable-line react-hooks/exhaustive-deps
  // Session 17: one client's plan; a switcher pick goes to the Inbox for the picked client.
  useFollowSwitcher(job?.client_id, () => '/');

  // Fix A: while the planner is generating or revising, show it and reload when it lands.
  const inFlight = job && ['queued', 'running', 'revising'].includes(job.status);
  useEffect(() => {
    if (!inFlight) return undefined;
    let cancelled = false;
    (async () => {
      const ok = await pollUntil(async () => {
        const { data } = await supabase.from('v_portal_plan_jobs').select('status').eq('job_id', jobId).maybeSingle();
        return data && !['queued', 'running', 'revising'].includes(data.status);
      }, { every: 15000, timeout: 30 * 60000 });
      if (cancelled) return;
      if (ok) { setBanner({ tone: 'ok', text: 'The plan is ready.' }); load(); }
    })();
    return () => { cancelled = true; };
  }, [inFlight, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAgency) return <Navigate to="/" replace />;
  if (error) return <div className="empty"><h2>Couldn't load the plan</h2><p>{error}</p></div>;
  if (job === null) return <p className="lede">Loading…</p>;
  if (job === false) return <div className="empty"><h2>Plan not found</h2></div>;

  const posts = job.posts || [];
  const isDL = job.client_id === 'digitallotus';
  // Session 21: a Digital Lotus plan sent for the owner's review is reviewed
  // here by the agency as DL's client (same per-post screen).
  const dlOwnerReview = isDL && job.status === 'sent';
  const reviewable = job.status === 'agency_review' || dlOwnerReview;
  const agencyOnly = job.approval_mode === 'agency_only';   // Session 13: the agency approval is final
  const dirty = Object.keys(drafts).length > 0;
  function setField(i, k, v) {
    const orig = posts[i]?.[k] ?? '';
    setDrafts((d) => {
      const row = { ...(d[i] || {}) };
      if (v === orig) delete row[k]; else row[k] = v;
      const next = { ...d };
      if (Object.keys(row).length) next[i] = row; else delete next[i];
      return next;
    });
  }
  async function saveEdits() {
    setBusy(true); setBanner(null);
    for (const [i, fields] of Object.entries(drafts)) {
      const { error: err } = await supabase.rpc('portal_edit_plan_item', { p_job_id: jobId, p_index: Number(i), p_fields: fields });
      if (err) { setBusy(false); setBanner({ tone: 'err', text: `Post ${Number(i) + 1}: ${err.message}` }); return; }
    }
    setBusy(false);
    setBanner({ tone: 'ok', text: 'Saved.' });
    load();
  }
  function submitted(_res, { replacements }) {
    if (replacements === 0) {
      setBanner({ tone: 'ok', text: isDL ? 'Approved. The weekly question jobs are being seeded.'
        : agencyOnly ? 'Approved (final). The question jobs are being seeded.' : `Approved and sent to ${job.client_name}.` });
    } else {
      setBanner({ tone: 'ok', text: `Sent. The planner is replacing ${replacements} post${replacements === 1 ? '' : 's'}; every other post stays as it is. You'll get an email when ${replacements === 1 ? "it's" : "they're"} ready.` });
    }
    load();
  }

  const statusText = {
    queued: 'Generating the plan…', running: 'Generating the plan…', agency_review: 'Waiting on your review.',
    revising: 'Revising with the latest notes…',
    sent: dlOwnerReview ? 'Waiting on your review as Digital Lotus.' : `Sent to ${job.client_name}. Waiting on their approval.`,
    approved: 'Approved. Posts are scheduled.', failed: `Generation failed: ${job.error || 'see email'}`,
  }[job.status] || job.status;
  const lastRev = (job.revisions || []).slice(-1)[0];

  const renderTitle = (p, i) => {
    if (!reviewable) return p.title;
    if (editing === i) {
      return (
        <div className="title-edit">
          <input value={drafts[i]?.title ?? p.title ?? ''} onChange={(e) => setField(i, 'title', e.target.value)} aria-label={`title for post ${i + 1}`} autoFocus />
          <input value={drafts[i]?.primary_keyword ?? p.primary_keyword ?? ''} onChange={(e) => setField(i, 'primary_keyword', e.target.value)} aria-label={`keyword for post ${i + 1}`} placeholder="primary keyword" />
          <button type="button" className="link-btn" onClick={() => setEditing(null)}>Done</button>
        </div>
      );
    }
    return (
      <>
        <span>{drafts[i]?.title ?? p.title}</span>
        {' '}<button type="button" className="link-btn title-edit-btn" onClick={() => setEditing(i)}>Edit title</button>
      </>
    );
  };

  return (
    <>
      <p><Link to={clientPath(`/admin/clients/${job.client_id}/settings`, job.client_id)}>← {job.client_name}</Link></p>
      <h1>{job.title || 'Quarterly plan'}</h1>
      <p className="lede">{statusText}</p>
      {job.status === 'sent' && !dlOwnerReview && (
        // Session 8 (B2): read-only once sent. No Approve / Change (reviewable is
        // false), just the banner. updated_at is the closest exposed sent time.
        <div className="banner banner-waiting">
          Sent to client{job.updated_at ? ` on ${fmtDate(job.updated_at)}` : ''}, waiting for client approval.
          {job.auto_approves && <DeadlineNote at={job.deadline_at} agency />}
        </div>
      )}
      {job.status === 'approved' && job.auto_approved_at && <AutoApprovedNote at={job.auto_approved_at} />}
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      {inFlight && <Working text={job.status === 'revising' ? 'Working on the revision. This can take a few minutes.' : 'Generating the plan. This can take a few minutes.'} />}

      {lastRev && (
        <section className="review-section">
          <h2>Latest notes ({lastRev.stage === 'client' ? job.client_name : 'you'})</h2>
          {lastRev.kind === 'per_post' ? (
            // Session 20: only the replaced posts, each with its own note
            <>
              {lastRev.overall_note && <p style={{ whiteSpace: 'pre-line' }}><strong>Overall:</strong> {lastRev.overall_note}</p>}
              <p className="hint" style={{ marginTop: 0 }}>Only these posts changed. Every other post is exactly as it was.</p>
            </>
          ) : <p style={{ whiteSpace: 'pre-line' }}>{lastRev.notes}</p>}
          {(lastRev.changed || []).length > 0 && (
            <ul>{lastRev.changed.map((c) => (
              <li key={c.index}>{fmtDate(c.publish_date)}: {c.old_title !== c.new_title
                ? <><s>{c.old_title}</s> → <strong>{c.new_title}</strong></>
                : <><strong>{c.new_title}</strong> ({c.fields.join(', ')} updated)</>}
                {(lastRev.targets || []).find((t) => t.index === c.index)?.note && (
                  <div className="hint" style={{ margin: '2px 0 0' }}>Note: {(lastRev.targets || []).find((t) => t.index === c.index).note}</div>
                )}</li>
            ))}</ul>
          )}
        </section>
      )}

      {job.status === 'approved' && !isDL && (
        <ApprovedReplace job={job} agencyOnly={agencyOnly} />
      )}

      {reviewable && dirty && (
        <div className="review-actions" style={{ justifyContent: 'flex-start', marginTop: 0 }}>
          <button type="button" className="btn" disabled={busy} onClick={saveEdits}>Save title edits</button>
          <button type="button" className="link-btn" onClick={() => { setDrafts({}); setEditing(null); }}>Undo edits</button>
        </div>
      )}

      {posts.length > 0 && (job.status !== 'approved' || isDL) && (
        <PostReview
          plan={{ ...job, posts: posts.map((p) => ({ ...p, why: p.why_now })),
                  // a plan approved as a whole (e.g. by email reply) has every post approved
                  post_reviews: job.status === 'approved'
                    ? Object.fromEntries(posts.map((p) => [p.slug, { state: 'approved', by_role: 'agency' }])) : job.post_reviews }}
          stage={job.status === 'sent' ? 'client' : 'agency'}
          readOnly={!reviewable}
          renderTitle={reviewable && !dlOwnerReview ? renderTitle : undefined}
          blocked={dirty ? 'Save or undo your title edits first.' : null}
          approveLabel={isDL ? 'Approve plan' : agencyOnly ? 'Approve (final)' : 'Approve plan and send to client'}
          hint={reviewable ? 'Approve or Replace each post. Replaced posts are regenerated by the planner; approved posts stay exactly as they are.'
            + (isDL || agencyOnly ? '' : ' Your approvals here are not the client\'s: they still decide every post.') : null}
          onSubmitted={submitted}
          clientId={activeId}
        />
      )}
    </>
  );
}

// Session 16: an approved plan. Every post whose questions haven't gone out can
// be replaced (same date). Session 19: every replacement waits here for review.
// agency_only: Approve applies it. client / client_deadline: Send to client,
// then the client approves that one item. A failed one can be dismissed or
// tried again (newest replacement per post only).
function ApprovedReplace({ job, agencyOnly }) {
  const [rows, setRows] = useState(null);
  const [reps, setReps] = useState({});
  const [banner, setBanner] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: pr, error }, { data: rd }] = await Promise.all([
        supabase.from('v_portal_plan').select('*').eq('plan_id', job.job_id),
        supabase.from('v_portal_replacements').select('*').eq('plan_id', job.job_id).order('created_at', { ascending: false }),
      ]);
      if (cancelled) return;
      if (error) { setBanner({ tone: 'err', text: error.message }); return; }
      setRows(Object.fromEntries((pr || []).map((r) => [r.slug, r])));
      // the newest replacement per post, while it is open (or just failed)
      const open = {}; const seen = new Set();
      for (const r of rd || []) {
        if (seen.has(r.slug)) continue;
        seen.add(r.slug);
        if (['queued', 'running', 'agency_review', 'sent', 'failed'].includes(r.status)) open[r.slug] = r;
      }
      setReps(open);
    })();
    return () => { cancelled = true; };
  }, [job.job_id, tick]);

  // while something is generating, refresh every 15s
  const generating = Object.values(reps).some((r) => ['queued', 'running'].includes(r.status));
  useEffect(() => {
    if (!generating) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [generating]);

  const done = (text) => { setBanner({ tone: 'ok', text }); setTick((n) => n + 1); };
  const history = job.replace_history || [];
  const posts = job.posts || [];

  return (
    <section className="review-section">
      <h2>Posts</h2>
      <p className="hint" style={{ marginTop: 0 }}>
        Replace any post whose questions haven't gone out yet. It keeps its date.
        {agencyOnly ? ' Agency approval: you review the new post here and approve it before it takes the slot.'
          : ' You review the new topic first, then the client approves that one post.'}
      </p>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      {generating && <Working text="Generating a replacement. This can take a few minutes." />}
      <ol className="plan-cards" aria-label="Posts in this plan">
        {posts.map((p) => {
          const row = rows?.[p.slug] || null;
          const rep = reps[p.slug];
          return (
            <li key={p.slug} className="plan-card">
              <div className="plan-card-main">
                <div className="plan-card-title">
                  {p.title}{row?.replaced && <span className="mode-tag">Replaced</span>}
                </div>
                <div className="plan-card-meta">
                  <span><strong>Publishes</strong> {fmtDate(p.publish_date)}</span>
                  {p.primary_keyword && <span><strong>Keyword / topic</strong> {p.primary_keyword}</span>}
                  {row && <span><strong>Status</strong> {row.status}</span>}
                </div>
                {p.why_now && <p className="plan-card-why">{p.why_now}</p>}
                {row && <ReplaceState row={row} isAgency />}
                {rep?.status === 'failed' && <FailedReplace rep={rep} row={row} agencyOnly={agencyOnly} onDone={done} />}
                {rep && <ReplaceReview rep={rep} isAgency onDone={done} old={p} />}
              </div>
              <div className="plan-card-actions">
                {row && rep?.status !== 'failed' && <ReplaceControl row={row} isAgency agencyOnly={agencyOnly} onDone={done} />}
                {row && !row.replaceable && !row.replace_status && <span className="hint" style={{ margin: 0 }}>Questions sent</span>}
              </div>
            </li>
          );
        })}
      </ol>
      {history.length > 0 && (
        <>
          <h2 style={{ marginTop: 24 }}>Replaced</h2>
          <ul>
            {history.slice().reverse().map((h, i) => (
              <li key={i}>{fmtDate(h.publish_date)}: <s>{h.old?.title}</s> → <strong>{h.new_title}</strong>
                {' '}<span className="hint" style={{ margin: 0 }}>({h.by === 'client' ? 'client approved' : h.by === 'auto_deadline' ? 'approved at the deadline' : 'agency'}{h.note ? `, note: ${h.note}` : ''})</span></li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
